import React, { useState } from "react";
import Select from "react-select";
import { Operation, PredefinedOp } from "../../interfaces/app_types";

interface BulkOperationsProps {
  predefinedOps: PredefinedOp[];
  opsLoading: boolean;
  opsError: boolean;
  selectedCount: number;
  isSaving: boolean;
  onApply: (operations: Operation[]) => void;
  onCancel: () => void;
}

const BulkOperations: React.FC<BulkOperationsProps> = ({
  predefinedOps,
  opsLoading,
  opsError,
  selectedCount,
  isSaving,
  onApply,
  onCancel,
}) => {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [customOperations, setCustomOperations] = useState<Operation[]>([]);
  const options = predefinedOps.map((op) => ({
    value: op.id,
    label: op.title,
  }));
  const updateCustom = (index: number, changes: Partial<Operation>) =>
    setCustomOperations((prev) =>
      prev.map((op, i) => (i === index ? { ...op, ...changes } : op))
    );
  const allOperations = [...operations, ...customOperations];
  const valid =
    allOperations.length > 0 &&
    allOperations.every(
      (op) =>
        typeof op.count === "number" &&
        Number.isFinite(op.count) &&
        op.count > 0 &&
        (!op.custom ||
          (op.title?.trim() &&
            typeof op.rate === "number" &&
            Number.isFinite(op.rate) &&
            op.rate >= 0))
    );

  return (
    <form
      className="costing-bulk-operations"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || !selectedCount || isSaving) return;
        onApply(
          allOperations.map((op) =>
            op.custom ? { ...op, title: op.title?.trim() } : op
          )
        );
      }}
    >
      <h4>
        Add operations to {selectedCount} selected component
        {selectedCount === 1 ? "" : "s"}
      </h4>
      <label htmlFor="bulk-operation-select">Choose operations</label>
      <Select
        inputId="bulk-operation-select"
        isMulti
        closeMenuOnSelect={false}
        options={options}
        value={options.filter((option) =>
          operations.some((op) => op.id === option.value)
        )}
        onChange={(selected) =>
          setOperations(
            selected.map(
              (option) =>
                operations.find((op) => op.id === option.value) ?? {
                  id: option.value,
                  custom: false,
                  count: 1,
                }
            )
          )
        }
        isLoading={opsLoading}
        isDisabled={opsLoading || opsError || isSaving}
        placeholder="Search and select operations…"
      />
      {opsError && (
        <p className="costing-hint costing-hint--error">
          Could not load operations. You can still add custom operations.
        </p>
      )}
      {operations.map((op, index) => (
        <div className="costing-bulk-operations__row" key={op.id}>
          <span>{options.find((option) => option.value === op.id)?.label}</span>
          <label>
            Count
            <input
              type="number"
              min="0.01"
              step="any"
              required
              disabled={isSaving}
              className="costing-input costing-input--count"
              value={op.count}
              aria-label={`Count for ${
                options.find((option) => option.value === op.id)?.label
              }`}
              onChange={(event) => {
                const count =
                  event.target.value === "" ? "" : Number(event.target.value);
                setOperations((prev) =>
                  prev.map((value, i) =>
                    i === index ? { ...value, count } : value
                  )
                );
              }}
            />
          </label>
        </div>
      ))}
      {customOperations.map((op, index) => (
        <div className="costing-bulk-operations__custom" key={index}>
          <label>
            Custom operation
            <input
              className="costing-input"
              placeholder="Operation title"
              required
              disabled={isSaving}
              value={op.title}
              onChange={(event) =>
                updateCustom(index, { title: event.target.value })
              }
            />
          </label>
          <label>
            Rate
            <input
              className="costing-input"
              type="number"
              min="0"
              step="any"
              required
              disabled={isSaving}
              value={op.rate}
              onChange={(event) =>
                updateCustom(index, {
                  rate:
                    event.target.value === "" ? "" : Number(event.target.value),
                })
              }
            />
          </label>
          <label>
            Unit
            <select
              className="costing-input"
              value={op.unit}
              disabled={isSaving}
              onChange={(event) =>
                updateCustom(index, { unit: event.target.value })
              }
            >
              <option value="per_piece">per piece</option>
              <option value="per_weight">per kg</option>
              <option value="fixed">fixed</option>
            </select>
          </label>
          <label>
            Count
            <input
              className="costing-input"
              type="number"
              min="0.01"
              step="any"
              required
              disabled={isSaving}
              value={op.count}
              onChange={(event) =>
                updateCustom(index, {
                  count:
                    event.target.value === "" ? "" : Number(event.target.value),
                })
              }
            />
          </label>
          <button
            type="button"
            className="costing-remove-btn"
            disabled={isSaving}
            aria-label={`Remove custom operation ${index + 1}`}
            onClick={() =>
              setCustomOperations((prev) => prev.filter((_, i) => i !== index))
            }
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="costing-sm-btn"
        disabled={isSaving}
        onClick={() =>
          setCustomOperations((prev) => [
            ...prev,
            { custom: true, title: "", rate: "", unit: "per_piece", count: 1 },
          ])
        }
      >
        + Custom operation
      </button>
      <p className="costing-hint">
        Existing operations are kept. These operations will be added to each
        selected component.
      </p>
      <div className="costing-bulk-operations__actions">
        <button
          type="submit"
          className="costing-sm-btn"
          disabled={!valid || !selectedCount || isSaving}
        >
          Apply to {selectedCount} component{selectedCount === 1 ? "" : "s"}
        </button>
        <button
          type="button"
          className="costing-reorder-btn"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default BulkOperations;
